import React from 'react';
import {
  Button,
  TextField,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Grid,
  Card,
  CardContent,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
} from '@material-ui/icons';

const Alert = ({ children, severity = 'info', icon }) => (
  <Paper style={{ marginTop: 8, padding: 8, backgroundColor: severity === 'info' ? '#e3f2fd' : '#e8f5e9' }}>
    <Typography variant="caption">
      {icon} {children}
    </Typography>
  </Paper>
);

const formatCurrencyInput = value => {
  if (!value && value !== 0) {
    return '';
  }
  // If value is already a decimal number (stored format like "6595.55")
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(numValue)) {
    return '';
  }
  return numValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const Calculo = ({
  formData,
  setFormData,
  addScale,
  removeScale,
  updateScale,
  classes,
  fieldErrors,
  validateField,
}) => {
  return (
    <div className={classes.sectionGap}>
      <div>
        <Typography variant="h6" gutterBottom>
          Tipo de Cálculo
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              className={`${classes.cardSelectable} ${
                formData.calculationType === 'fixed' ? classes.cardSelected : ''
              }`}
              onClick={() => {
                setFormData({ ...formData, calculationType: 'fixed' });
              }}
            >
              <CardContent style={{ padding: 12, textAlign: 'center' }}>
                <div
                  className={classes.cardIcon}
                  style={{
                    backgroundColor: formData.calculationType === 'fixed' ? '#1976d2' : '#bdbdbd',
                  }}
                >
                  <MoneyIcon style={{ color: 'white', fontSize: 24 }} />
                </div>
                <Typography variant="subtitle1" gutterBottom>
                  Valor Fixo
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Comissão fixa ou percentual único
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              className={`${classes.cardSelectable} ${
                formData.calculationType === 'scale' ? classes.cardSelected : ''
              }`}
              onClick={() => {
                setFormData({ ...formData, calculationType: 'scale' });
              }}
            >
              <CardContent style={{ padding: 12, textAlign: 'center' }}>
                <div
                  className={classes.cardIcon}
                  style={{
                    backgroundColor: formData.calculationType === 'scale' ? '#1976d2' : '#bdbdbd',
                  }}
                >
                  <TrendingUpIcon style={{ color: 'white', fontSize: 30 }} />
                </div>
                <Typography variant="subtitle1" gutterBottom>
                  Escalonado
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Comissão por faixas de MWh
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </div>

      {formData.calculationType === 'fixed' && (
        <Paper variant="outlined" className={classes.configSection}>
          <Typography variant="subtitle1" gutterBottom>
            Configuração de Valor Fixo
          </Typography>
          <Grid container spacing={2} style={{ marginBottom: 12 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Tipo de Comissão</InputLabel>
                <Select
                  value={formData.fixedType}
                  label="Tipo de Comissão"
                  onChange={e => setFormData({ ...formData, fixedType: e.target.value })}
                >
                  <MenuItem value="fixed">Valor Fixo (R$)</MenuItem>
                  <MenuItem value="percent">Percentual (%)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              {formData.fixedType === 'fixed' ? (
                <TextField
                  label="Valor (R$)"
                  value={formatCurrencyInput(formData.fixedValue)}
                  onChange={e => {
                    // Remove tudo exceto dígitos
                    const rawValue = e.target.value.replace(/\D/g, '');
                    // Converte centavos para reais (ex: 659555 -> 6595.55)
                    const numericValue = rawValue ? parseInt(rawValue, 10) / 100 : '';
                    const newFormData = { ...formData, fixedValue: numericValue };
                    setFormData(newFormData);
                    validateField('fixedValue', numericValue, newFormData);
                  }}
                  onBlur={() => validateField('fixedValue', formData.fixedValue)}
                  placeholder="Ex: 1.500,00"
                  required={formData.calculationType === 'fixed'}
                  fullWidth
                  variant="outlined"
                  error={!!fieldErrors?.fixedValue}
                  helperText={fieldErrors?.fixedValue || 'Digite o valor em reais'}
                  InputProps={{
                    startAdornment: <span style={{ marginRight: 4, color: '#666' }}>R$</span>,
                  }}
                />
              ) : (
                <TextField
                  label="Percentual (%)"
                  type="number"
                  value={formData.fixedValue}
                  onChange={e => {
                    const newFormData = { ...formData, fixedValue: e.target.value };
                    setFormData(newFormData);
                    validateField('fixedValue', e.target.value, newFormData);
                  }}
                  onBlur={() => validateField('fixedValue', formData.fixedValue)}
                  placeholder="Ex: 5.5"
                  required={formData.calculationType === 'fixed'}
                  fullWidth
                  variant="outlined"
                  error={!!fieldErrors?.fixedValue}
                  helperText={fieldErrors?.fixedValue || ''}
                  InputProps={{
                    endAdornment: <span style={{ marginLeft: 4, color: '#666' }}>%</span>,
                    inputProps: { step: '0.01', min: 0, max: 100 },
                  }}
                />
              )}
            </Grid>
          </Grid>
          <Alert severity="info" icon="💡">
            {formData.fixedType === 'fixed'
              ? 'Este valor fixo será aplicado como comissão por contrato'
              : 'Este percentual será aplicado sobre o valor do contrato'}
          </Alert>
        </Paper>
      )}

      {formData.calculationType === 'scale' && (
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <div>
              <Typography variant="h6">Escalonamento por KWh</Typography>
              <Typography variant="body2" color="textSecondary">
                Defina as faixas de volumes e percentuais de comissão
              </Typography>
            </div>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addScale}
              style={{ backgroundColor: '#FF5246', color: '#fff' }}
            >
              Adicionar Faixa
            </Button>
          </div>

          {formData.scales.map((scale, index) => (
            <Paper key={scale.id || `scale-${index}`} variant="outlined" className={classes.scaleItem}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 12,
                }}
              >
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  Faixa #{index + 1}
                </Typography>
                <IconButton
                  onClick={() => removeScale(index)}
                  disabled={formData.scales.length === 1}
                  color="secondary"
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </div>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Volume Mínimo (KWh)"
                    type="number"
                    inputProps={{ step: '1' }}
                    value={scale.minValue}
                    onChange={e => {
                      updateScale(index, 'minValue', e.target.value);
                      setTimeout(() => {
                        const newFormData = { ...formData };
                        const newScales = [...newFormData.scales];
                        newScales[index].minValue = e.target.value;
                        validateField('scales', newScales, { ...newFormData, scales: newScales });
                      }, 0);
                    }}
                    onBlur={() => validateField('scales', formData.scales)}
                    placeholder="Ex: 1000"
                    required={formData.calculationType === 'scale'}
                    fullWidth
                    size="small"
                    variant="outlined"
                    error={!!fieldErrors?.scales && (!scale.minValue || scale.minValue.trim() === '')}
                    helperText={
                      fieldErrors?.scales && (!scale.minValue || scale.minValue.trim() === '')
                        ? 'Volume mínimo é obrigatório'
                        : ''
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Volume Máximo (KWh)"
                    type="number"
                    inputProps={{ step: '1' }}
                    value={scale.maxValue}
                    onChange={e => {
                      updateScale(index, 'maxValue', e.target.value);
                      setTimeout(() => {
                        const newFormData = { ...formData };
                        const newScales = [...newFormData.scales];
                        newScales[index].maxValue = e.target.value;
                        validateField('scales', newScales, { ...newFormData, scales: newScales });
                      }, 0);
                    }}
                    onBlur={() => validateField('scales', formData.scales)}
                    placeholder="Ex: 2000"
                    required={formData.calculationType === 'scale'}
                    fullWidth
                    size="small"
                    variant="outlined"
                    error={!!fieldErrors?.scales && (!scale.maxValue || scale.maxValue.trim() === '')}
                    helperText={
                      fieldErrors?.scales && (!scale.maxValue || scale.maxValue.trim() === '')
                        ? 'Volume máximo é obrigatório'
                        : ''
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Percentual (%)"
                    type="number"
                    inputProps={{ step: '0.01' }}
                    value={scale.percent}
                    onChange={e => {
                      updateScale(index, 'percent', e.target.value);
                      setTimeout(() => {
                        const newFormData = { ...formData };
                        const newScales = [...newFormData.scales];
                        newScales[index].percent = e.target.value;
                        validateField('scales', newScales, { ...newFormData, scales: newScales });
                      }, 0);
                    }}
                    onBlur={() => validateField('scales', formData.scales)}
                    placeholder="Ex: 60"
                    required={formData.calculationType === 'scale'}
                    fullWidth
                    size="small"
                    variant="outlined"
                    error={!!fieldErrors?.scales && (!scale.percent || scale.percent.trim() === '')}
                    helperText={
                      fieldErrors?.scales && (!scale.percent || scale.percent.trim() === '')
                        ? 'Percentual é obrigatório'
                        : ''
                    }
                  />
                </Grid>
              </Grid>
              {scale.minValue && scale.maxValue && scale.percent && (
                <Alert severity="info" icon="📊">
                  De {scale.minValue} até {scale.maxValue} KWh = {scale.percent}% de comissão
                </Alert>
              )}
            </Paper>
          ))}

          {fieldErrors?.scales && (
            <Typography variant="caption" color="error" style={{ marginTop: 8, display: 'block' }}>
              {fieldErrors.scales}
            </Typography>
          )}
          {formData.scales.length === 0 && (
            <div className={classes.emptyState}>
              <Typography variant="body1">Nenhuma faixa configurada</Typography>
              <Typography variant="caption" color="textSecondary">
                Clique em Adicionar Faixa para começar
              </Typography>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Calculo;
